import Appointment from './Appointment';
import deepmerge from 'deepmerge';

export interface Documents {
  mutual: string | null;
  insurance: string | null;
}

export type IPatientEntity = {
  id: string | number;
  name: string;
  firstname: string;
  birthname: string;
  gender: string;
  birthdate: string;
  nir: string;
  nir_alternative?: string;
  mutual?: string;
  ticketModerateur?: string;
  phone: string;
  email: string;
  referring_doctor: string;
  address: {
    line_1: string;
    line_2: string;
    postcode: string;
    city: string;
    country: string;
  };
  appointments: string;
  sidep: string;
};

export interface IPatient {
  id: string | number;
  lastName: string;
  firstName: string;
  birthName: string;
  gender: string;
  birthdate: Date;
  nir: string;
  nirAlternative?: File;
  mutual?: File | null;
  ticketModerateur?: File;
  phone: string;
  email: string;
  referringDoctor: string;
  address: {
    line1?: string;
    line2?: string;
    postcode?: string;
    city?: string;
    country?: string;
  };
  documents?: Documents;
  appointments: Appointment[];
  sidep?: object;
}

export default class Patient implements IPatient {
  id: string | number = '';
  lastName: string = '';
  firstName: string = '';
  birthName: string = '';
  gender: string = '';
  birthdate: Date = new Date();
  nir: string = '';
  nirAlternative?: File;
  mutual?: File;
  ticketModerateur?: File;
  phone: string = '';
  email: string = '';
  referringDoctor: string = '';
  address: {
    line1?: string;
    line2?: string;
    postcode?: string;
    city?: string;
    country?: string;
  } = {};
  appointments: Appointment[] = [];
  documents: Documents = {
    insurance: null,
    mutual: null
  };
  sidep?: object;

  constructor(obj: Partial<IPatient>) {
    Object.assign(this, obj);
  }

  update(obj: Partial<IPatient>) {
    Object.assign(this, obj);
    return this;
  }

  copyWith(obj: Partial<IPatient>) {
    return new Patient(deepmerge(Object.assign({}, this), obj));
  }

  static createFromEntity(obj: Partial<any>) {
    return new Patient({
      id: obj.id,
      lastName: obj.name,
      firstName: obj.firstname,
      birthName: obj.birthname,
      gender: obj.gender,
      birthdate: new Date(obj.birthdate),
      nir: obj.nir,
      nirAlternative: obj.nir_alternative,
      mutual: obj.mutual,
      ticketModerateur: obj.ticket_moderateur,
      phone: obj.phone,
      email: obj.email,
      referringDoctor: obj.referring_doctor,
      address: {
        line1: obj.address.line_1,
        line2: obj.address.line_2,
        postcode: obj.address.postcode,
        city: obj.address.city,
        country: obj.address.country
      },
      appointments:
        obj.appointments.map((appointment: any) =>
          Appointment.createFromEntity(appointment)
        ) || [],
      documents: obj.documents
    });
  }

  toEntity() {
    return {
      id: this.id,
      name: this.lastName,
      firstname: this.firstName,
      birthname: this.birthName,
      gender: ['m', 'f'].indexOf(this.gender) !== -1 ? this.gender : null,
      birthdate: this.birthdate,
      nir: this.nir,
      nir_alternative: this.nirAlternative,
      mutual: this.mutual,
      ticket_moderateur: this.ticketModerateur,
      phone: this.phone,
      email: this.email,
      referring_doctor: this.referringDoctor,
      address: {
        line_1: this.address.line1,
        line_2: this.address.line2,
        postcode: this.address.postcode,
        city: this.address.city,
        country: 'fr'
      },
      appointments: this.appointments,
      documents: this.documents,
      sidep: this.sidep ? JSON.stringify(this.sidep) : undefined
    };
  }

  getFullName() {
    return `${this.firstName} ${this.lastName}`;
  }

  getInitials() {
    return `${(this.firstName || '')[0] || ''}${
      (this.lastName || '')[0] || ''
    }`.toUpperCase();
  }
}

export type PatientToUpdateStringAttributes =
  | 'id'
  | 'lastName'
  | 'firstName'
  | 'gender'
  | 'birthdate'
  | 'nir'
  | 'phone'
  | 'email'
  | 'referringDoctor'
  | 'address'
  | 'insurance'
  | 'documents'
  | 'mutual';

export class PatientToUpdate {
  id?: string | number;
  lastName?: string;
  firstName?: string;
  birthName?: string;
  gender?: string;
  birthdate?: Date;
  nir?: string;
  nirAlternative?: File;
  mutual?: File;
  phone?: string;
  email?: string;
  referringDoctor?: string;
  address: {
    line1?: string;
    line2?: string;
    postcode?: string;
    city?: string;
    country?: string;
  } = {};
  insurance?: File;
  sidep?: Object;
  // appointments?: Appointment[];
  constructor(obj: Partial<PatientToUpdate>) {
    Object.assign(this, obj);
  }

  toEntity() {
    let entity: any = {
      name: this.lastName,
      firstname: this.firstName,
      birthname: this.birthName,
      gender: this.gender,
      birthdate: this.birthdate?.toISOString(),
      nir: this.nir,
      phone: this.phone,
      email: this.email,
      referring_doctor: this.referringDoctor,
      address: {
        line_1: this.address.line1,
        line_2: this.address.line2,
        postcode: this.address.postcode,
        city: this.address.city,
        country: this.address.country
      },
      insurance: this.insurance,
      mutual: this.mutual
    };

    if (this.sidep && Object.keys(this.sidep).length !== 0) {
      entity.sidep = JSON.stringify(this.sidep);
    }

    return entity;
  }

  copyWith(obj: Partial<PatientToUpdate>): PatientToUpdate {
    return new PatientToUpdate(deepmerge(Object.assign({}, this), obj));
  }
}
